import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, CreditCard as Edit, Trash2, Save, User, Download, ArrowLeft } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { toast } from '@shared/components/ui/Toaster';
import { Button } from '@shared/components/ui/Button';
import { Input } from '@shared/components/ui/Input';
import { Modal } from '@shared/components/ui/Modal';

interface Persona {
  id: string;
  name: string;
  description: string | null;
  content: string;
  created_at: string;
  updated_at: string;
}

const AdminPersonas: React.FC = () => {
  const navigate = useNavigate();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [filteredPersonas, setFilteredPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    content: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPersonas();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredPersonas(personas);
    } else {
      const filtered = personas.filter(
        (persona) =>
          persona.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (persona.description && persona.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredPersonas(filtered);
    }
  }, [searchTerm, personas]);

  const loadPersonas = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('personas')
        .select('*')
        .order('name', { ascending: true });

      if (error) throw error;

      setPersonas(data || []);
      setFilteredPersonas(data || []);
    } catch (error: any) {
      console.error('Error loading personas:', error);
      toast.error('Failed to load personas');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPersona(null);
    setFormData({
      name: '',
      description: '',
      content: ''
    });
    setShowModal(true);
  };

  const handleEdit = (persona: Persona) => {
    setEditingPersona(persona);
    setFormData({
      name: persona.name,
      description: persona.description || '',
      content: persona.content
    });
    setShowModal(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.content) {
      toast.error('Name and content are required');
      return;
    }

    try {
      setSaving(true);
      console.log('💾 Saving persona:', editingPersona ? 'UPDATE' : 'INSERT', formData.name);

      const personaData = {
        name: formData.name.trim(),
        description: formData.description?.trim() || null,
        content: formData.content.trim(),
        updated_at: new Date().toISOString()
      };

      // Add timeout protection for the save operation
      const savePromise = editingPersona
        ? supabase.from('personas').update(personaData).eq('id', editingPersona.id)
        : supabase.from('personas').insert({ ...personaData, created_at: new Date().toISOString() });

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Save operation timed out after 15 seconds')), 15000)
      );

      const { error } = await Promise.race([savePromise, timeoutPromise]) as any;

      if (error) {
        console.error('❌ Save error:', error);

        // Provide more specific error messages
        if (error.code === '23505') {
          toast.error(`A persona with the name "${formData.name}" already exists`);
        } else if (error.message?.includes('permission denied') || error.message?.includes('policy')) {
          toast.error('You do not have permission to perform this action. Please verify your admin status.');
          console.error('🚫 RLS Policy Error - User may not have admin rights');
        } else if (error.message?.includes('timeout')) {
          toast.error('The operation took too long. Please try again.');
        } else {
          toast.error(error.message || 'Failed to save persona');
        }
        throw error;
      }

      console.log('✅ Persona saved successfully');
      toast.success(editingPersona ? 'Persona updated successfully' : 'Persona created successfully');
      setShowModal(false);
      loadPersonas();
    } catch (error: any) {
      console.error('❌ Error saving persona:', error);

      // Only show toast if we haven't already shown one
      if (!error.message?.includes('policy') && !error.code) {
        toast.error(error.message || 'Failed to save persona');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (personaId: string, personaName: string) => {
    if (!confirm(`Are you sure you want to delete the persona "${personaName}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('personas')
        .delete()
        .eq('id', personaId);

      if (error) throw error;

      toast.success('Persona deleted successfully');
      loadPersonas();
    } catch (error: any) {
      console.error('Error deleting persona:', error);
      toast.error('Failed to delete persona');
    }
  };

  const handleExport = async () => {
    try {
      const { exportPersonasConfiguration } = await import('../../utils/configurationExporter');
      const result = await exportPersonasConfiguration();

      if (result.success) {
        toast.success(`Exported ${result.filename} successfully!`);
      } else {
        toast.error(result.error || 'Failed to export personas');
      }
    } catch (error: any) {
      console.error('Error exporting:', error);
      toast.error('Failed to export personas');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Persona Management</h1>
            <p className="text-gray-600">Manage AI personas and their characteristics</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleExport} variant="outline" className="flex items-center gap-2">
              <Download className="w-4 h-4" />
              Export Personas
            </Button>
            <Button onClick={handleCreateNew} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Persona
            </Button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Search personas by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPersonas.map((persona) => (
          <div key={persona.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-3 rounded-lg">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{persona.name}</h3>
                  <p className="text-sm text-gray-500">
                    {new Date(persona.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {persona.description && (
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{persona.description}</p>
            )}

            <div className="mb-4">
              <p className="text-xs text-gray-500 mb-1">Content Preview:</p>
              <p className="text-sm text-gray-700 line-clamp-3 bg-gray-50 p-2 rounded">
                {persona.content}
              </p>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
              <button
                onClick={() => handleEdit(persona)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button
                onClick={() => handleDelete(persona.id, persona.name)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredPersonas.length === 0 && (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No personas found</p>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingPersona ? 'Edit Persona' : 'Create New Persona'}
        size="xl"
      >
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Name *
            </label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={2}
              placeholder="Brief description of this persona..."
            />
          </div>

          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Persona Content *
            </label>
            <textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent font-mono text-sm"
              rows={12}
              required
              placeholder="Enter the persona definition, characteristics, and instructions..."
            />
            <p className="text-xs text-gray-500 mt-1">
              Define the AI persona's personality, expertise, communication style, and behavior.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-4">
            <Button
              type="submit"
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : editingPersona ? 'Update Persona' : 'Create Persona'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default AdminPersonas;
